import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import Body from "src/components/common/Body";
import Button from "src/components/common/Button";
import ConfirmModal from "src/components/common/ConfirmModal";
import { useGetOftenUsePhrasesQuery, useAddOftenUsePhraseMutation, useUpdateOftenUsePhraseMutation, useDeleteOftenUsePhraseMutation } from "src/api/noticeAPI";
import { RotatingLines } from "react-loader-spinner";

interface Phrase {
  phrase_idx: number;
  user_id: string;
  phrase: string;
}

function PhrasesSetting() {
  const navigate = useNavigate();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [newPhrase, setNewPhrase] = useState("");
  const [editingPhrase, setEditingPhrase] = useState<Phrase | null>(null);
  const [deletingPhrase, setDeletingPhrase] = useState<Phrase | null>(null);
  
  const { data: phrases = [], isLoading, refetch } = useGetOftenUsePhrasesQuery(undefined);
  const [addPhrase, { isLoading: isAddLoading }] = useAddOftenUsePhraseMutation();
  const [updatePhrase, { isLoading: isUpdateLoading }] = useUpdateOftenUsePhraseMutation();
  const [deletePhrase, { isLoading: isDeleteLoading }] = useDeleteOftenUsePhraseMutation();

  const handleBack = () => {
    navigate(-1);
  };

  const handleAddPhrase = async () => {
    if (newPhrase.trim().length === 0) {
      alert("문구를 입력해주세요.");
      return;
    }

    if (new Blob([newPhrase]).size > 128) {
      alert("문구는 128byte를 초과할 수 없습니다.");
      return;
    }

    try {
      const result = await addPhrase({ phraseText: newPhrase });
      
      if ("data" in result && result.data) {
        setNewPhrase("");
        setIsAddModalOpen(false);
        refetch();
        alert("문구가 추가되었습니다.");
      } else if ("error" in result) {
        alert("문구 추가에 실패했습니다.");
      } else {
        alert("문구 추가에 실패했습니다.");
      }
    } catch (error) {
      alert("문구 추가 중 오류가 발생했습니다.");
    }
  };

  const handleEditPhrase = async () => {
    if (!editingPhrase || editingPhrase.phrase.trim().length === 0) {
      alert("문구를 입력해주세요.");
      return;
    }

    if (new Blob([editingPhrase.phrase]).size > 128) {
      alert("문구는 128byte를 초과할 수 없습니다.");
      return;
    }

    try {
      const result = await updatePhrase({
        phraseIdx: editingPhrase.phrase_idx,
        phraseText: editingPhrase.phrase,
      });
      
      if ("data" in result && result.data) {
        setIsEditModalOpen(false);
        setEditingPhrase(null);
        refetch();
        alert("문구가 수정되었습니다.");
      } else if ("error" in result) {
        alert("문구 수정에 실패했습니다.");
      } else {
        alert("문구 수정에 실패했습니다.");
      }
    } catch (error) {
      alert("문구 수정 중 오류가 발생했습니다.");
    }
  };

  const handleDeletePhrase = async () => {
    if (!deletingPhrase) return;

    try {
      const result = await deletePhrase({ phraseIdx: deletingPhrase.phrase_idx });
      
      if ("data" in result && result.data) {
        setIsDeleteModalOpen(false);
        setDeletingPhrase(null);
        refetch();
        alert("문구가 삭제되었습니다.");
      } else if ("error" in result) {
        alert("문구 삭제에 실패했습니다.");
      } else {
        alert("문구 삭제에 실패했습니다.");
      }
    } catch (error) {
      alert("문구 삭제 중 오류가 발생했습니다.");
    }
  };

  const openEditModal = (phrase: Phrase) => {
    setEditingPhrase({ ...phrase });
    setIsEditModalOpen(true);
  };

  const openDeleteModal = (phrase: Phrase) => {
    setDeletingPhrase(phrase);
    setIsDeleteModalOpen(true);
  };

  return (
    <>
      <header>
        <div>
          <Button variant="text" className="btn-back" onClick={handleBack}>
            자주 쓰는 문구 설정
          </Button>
        </div>
        <div className="btn-box">
          <Button variant="text" onClick={() => setIsAddModalOpen(true)}>
            추가
          </Button>
        </div>
      </header>

      <Body>
        <Container>
          {isLoading ? (
            <LoadingWrapper>
              <RotatingLines width="40" strokeColor="#00263e" strokeWidth="2" />
            </LoadingWrapper>
          ) : phrases.length === 0 ? (
            <EmptyState>
              <p>등록된 문구가 없습니다.</p>
              <p>자주 사용하는 문구를 추가해보세요.</p>
            </EmptyState>
          ) : (
            <PhraseList>
              {phrases.map((phrase: Phrase) => (
                <PhraseItem key={phrase.phrase_idx}>
                  <PhraseText>{phrase.phrase}</PhraseText>
                  <ButtonGroup>
                    <EditButton onClick={() => openEditModal(phrase)}>
                      수정
                    </EditButton>
                    <DeleteButton onClick={() => openDeleteModal(phrase)}>
                      삭제
                    </DeleteButton>
                  </ButtonGroup>
                </PhraseItem>
              ))}
            </PhraseList>
          )}
        </Container>
      </Body>

      {/* 추가 모달 */}
      <ConfirmModal
        isOpen={isAddModalOpen}
        title="문구 추가"
        onClose={() => setIsAddModalOpen(false)}
        onConfirm={handleAddPhrase}
        confirmText="추가"
        isLoading={isAddLoading}
      >
        <ModalContent>
          <StyledTextarea
            value={newPhrase}
            onChange={(e) => setNewPhrase(e.target.value)}
            placeholder="자주 사용하는 문구를 입력해주세요."
            maxLength={128}
          />
          <CharCount>
            {new Blob([newPhrase]).size}/128 byte
          </CharCount>
        </ModalContent>
      </ConfirmModal>

      {/* 수정 모달 */}
      <ConfirmModal
        isOpen={isEditModalOpen}
        title="문구 수정"
        onClose={() => setIsEditModalOpen(false)}
        onConfirm={handleEditPhrase}
        confirmText="수정"
        isLoading={isUpdateLoading}
      >
        <ModalContent>
          <StyledTextarea
            value={editingPhrase?.phrase || ""}
            onChange={(e) =>
              setEditingPhrase((prev) =>
                prev ? { ...prev, phrase: e.target.value } : null
              )
            }
            placeholder="문구를 수정해주세요."
            maxLength={128}
          />
          <CharCount>
            {new Blob([editingPhrase?.phrase || ""]).size}/128 byte
          </CharCount>
        </ModalContent>
      </ConfirmModal>

      {/* 삭제 확인 모달 */}
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        title="문구 삭제"
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeletePhrase}
        confirmText="삭제"
        isLoading={isDeleteLoading}
      >
        <ModalContent>
          <DeleteConfirmText>
            "{deletingPhrase?.phrase}" 문구를 삭제하시겠습니까?
          </DeleteConfirmText>
        </ModalContent>
      </ConfirmModal>
    </>
  );
}

const Container = styled.div`
  padding: 20px;
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${({ theme }) => theme.color.tertiary};

  p {
    margin-bottom: 8px;
    font-size: 16px;
    line-height: 1.5;

    &:first-child {
      font-weight: 600;
    }
  }
`;

const PhraseList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const PhraseItem = styled.li`
  display: flex;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid ${({ theme }) => theme.color.linePrimary};

  &:last-child {
    border-bottom: none;
  }
`;

const PhraseText = styled.div`
  flex: 1;
  font-size: 16px;
  line-height: 1.5;
  color: ${({ theme }) => theme.color.primary};
  word-break: break-all;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const EditButton = styled.button`
  padding: 6px 12px;
  border: 1px solid ${({ theme }) => theme.color.main};
  border-radius: 4px;
  background: transparent;
  color: ${({ theme }) => theme.color.main};
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.color.main};
    color: white;
  }
`;

const DeleteButton = styled.button`
  padding: 6px 12px;
  border: 1px solid #dc3545;
  border-radius: 4px;
  background: transparent;
  color: #dc3545;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #dc3545;
    color: white;
  }
`;

const ModalContent = styled.div`
  padding: 20px 0;
`;

const StyledTextarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.color.linePrimary};
  border-radius: 4px;
  font-size: 16px;
  line-height: 1.5;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.color.main};
  }

  &::placeholder {
    color: ${({ theme }) => theme.color.tertiary};
  }
`;

const CharCount = styled.div`
  margin-top: 8px;
  text-align: right;
  font-size: 12px;
  color: ${({ theme }) => theme.color.quaternary};
`;

const DeleteConfirmText = styled.div`
  text-align: center;
  font-size: 16px;
  line-height: 1.5;
  color: ${({ theme }) => theme.color.primary};
  word-break: break-all;
`;

export default PhrasesSetting;